<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="IPマッピング設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- 名前 -->
                <v-row>
                  <v-col v-if="ipMapping">
                    <app-label-group
                      label="名前"
                      :value="ipMappingInfo.ipMappingName"
                    />
                  </v-col>
                  <v-col v-else>
                    <app-badge
                      item="名前"
                      :msg="
                        $filter.message('info_alpha_num', { maxLength: '63' })
                      "
                      placement="end"
                    />
                    <validation-provider
                      name="ipMappingName"
                      v-model="ipMappingInfo.ipMappingName"
                      label="名前"
                      :rules="{
                        required: true,
                        between_length: { min: 1, max: 63 },
                        is_half_width_alpha_numeric: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- グローバルIP -->
                <v-row>
                  <v-col v-if="ipMapping">
                    <app-label-group
                      label="グローバルIP"
                      :value="ipMappingInfo.globalIpAddress"
                    />
                  </v-col>
                  <v-col v-else>
                    <app-badge item="グローバルIP" :is-info="false" />
                    <validation-provider
                      name="globalIpAddress"
                      v-model="ipMappingInfo.globalIpAddress"
                      label="グローバルIP"
                      :rules="{
                        required: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select
                        :items="globalIpList"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- 宛先ゾーン -->
                <v-row>
                  <v-col v-if="ipMapping && ipMappingInfo.dstZone">
                    <app-label-group
                      label="宛先ゾーン"
                      :value="ipMappingInfo.dstZone.zone.zoneName"
                    />
                  </v-col>
                  <v-col v-else-if="!ipMapping">
                    <app-badge item="宛先ゾーン" :is-info="false" />
                    <validation-provider
                      name="dstZone"
                      v-model="ipMappingInfo.dstZone"
                      label="宛先ゾーン"
                      :rules="{
                        required: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select
                        :items="dstZone"
                        return-object
                        item-value="zone.zoneId"
                        item-title="zone.zoneName"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      >
                      </app-select>
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- プライベートIP -->
                <v-row>
                  <v-col v-if="ipMapping && !ipMappingInfo.dstZone">
                    <app-label-group
                      label="プライベートIP"
                      :value="ipMappingInfo.privateIpAddress"
                    />
                  </v-col>
                  <v-col v-else>
                    <app-badge item="プライベートIP" :is-info="false" />
                    <validation-provider
                      name="ipAddressId"
                      v-model="ipMappingInfo.ipAddressId"
                      label="プライベートIP"
                      :rules="{
                        required: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select
                        :items="privateAddresses"
                        item-value="ipAddressId"
                        item-title="ipAddress"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- ポートフォワード -->
                <template v-if="ipMapping">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="ポートフォワード"
                        :value="
                          $filter.enumTo(ipMappingInfo.isPortForward, 'enable')
                        "
                      />
                    </v-col>
                  </v-row>
                  <template v-if="ipMappingInfo.isPortForward">
                    <v-row>
                      <v-col>
                        <app-label-group
                          label="プロトコル"
                          :value="ipMappingInfo.protocol"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <app-label-group
                          label="グローバル側Port"
                          :value="ipMappingInfo.globalPort"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <app-label-group
                          label="プライベート側Port"
                          :value="ipMappingInfo.privatePort"
                        />
                      </v-col>
                    </v-row>
                  </template>
                </template>
                <template v-else>
                  <v-row>
                    <v-col>
                      <app-badge
                        item="ポートフォワード"
                        :isRequired="false"
                        :isInfo="false"
                      />
                      <v-radio-group
                        inline
                        v-model="ipMappingInfo.isPortForward"
                        hide-details
                      >
                        <v-radio
                          v-for="option in portForwardOptions"
                          :key="option.text"
                          :label="option.text"
                          :value="option.val"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>
                  <template v-if="ipMappingInfo.isPortForward">
                    <v-row>
                      <v-col>
                        <app-badge
                          item="プロトコル"
                          :isRequired="false"
                          :isInfo="false"
                        />
                        <v-radio-group
                          inline
                          v-model="ipMappingInfo.protocol"
                          hide-details
                        >
                          <v-radio
                            v-for="option in protocolOptions"
                            :key="option.text"
                            :label="option.text"
                            :value="option.val"
                          />
                        </v-radio-group>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <app-badge
                          item="グローバル側Port"
                          :msg="$filter.message('info_global_private_port')"
                          placement="end"
                        />
                        <validation-provider
                          name="globalPort"
                          v-model="ipMappingInfo.globalPort"
                          label="グローバル側Port"
                          :rules="
                            isPrivateByRange
                              ? {
                                  required: true,
                                  is_port: { minVal: 1 },
                                  confirmed: {
                                    target: '@privatePort',
                                    targetField: 'プライベート側Port',
                                  },
                                }
                              : { required: true, is_port: { minVal: 1 } }
                          "
                          v-slot="{ componentField, errorMessage, meta }"
                        >
                          <app-form-input
                            v-bind="componentField"
                            :field-meta="meta"
                            :error-messages="errorMessage"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <app-badge
                          item="プライベート側Port"
                          :msg="$filter.message('info_global_private_port')"
                          placement="end"
                        />
                        <validation-provider
                          name="privatePort"
                          v-model="ipMappingInfo.privatePort"
                          label="プライベート側Port"
                          :rules="
                            isGlobalByRange
                              ? {
                                  required: true,
                                  is_port: { minVal: 1 },
                                  confirmed: {
                                    target: '@globalPort',
                                    targetField: 'グローバル側Port',
                                  },
                                }
                              : { required: true, is_port: { minVal: 1 } }
                          "
                          v-slot="{ componentField, errorMessage, meta }"
                        >
                          <app-form-input
                            v-bind="componentField"
                            :field-meta="meta"
                            :error-messages="errorMessage"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </template>
                </template>
                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :isRequired="false"
                      placement="end"
                    />
                    <validation-provider
                      name="description"
                      v-model="ipMappingInfo.description"
                      label="備考"
                      :rules="{
                        max: { length: 20 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  ipMapping ? "変更" : "追加"
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import cloneDeep from "lodash/cloneDeep";
import type { Type1IpMappingItem } from "@/views/nwSetting/applianceObject/Type1IpMapping.vue";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type { Type1ExtraInfo, Type1PrivateInfo } from "@/apis/Type1SiteApi";

export default defineComponent({
  name: "IpMappingType1Edit",
  props: {
    ipMapping: {
      type: Object as PropType<Type1IpMappingItem>,
      required: false,
      default: null,
    },
    ipMappingKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      ipMappingInfo: this.ipMapping
        ? cloneDeep(this.ipMapping)
        : ({
            isPortForward: false,
            protocol: "TCP",
          } as Type1IpMappingItem),
      globalIpList: [] as string[],
      zoneList: [] as ZoneRelationItem[],
      type1Extra: {} as Type1ExtraInfo,
      type1Private: {} as Type1PrivateInfo,
      portForwardOptions: [
        { text: "無効", val: false },
        { text: "有効", val: true },
      ],
      protocolOptions: [
        { text: "TCP", val: "TCP" },
        { text: "UDP", val: "UDP" },
      ],
      isLoaded: false,
    };
  },
  computed: {
    /** 宛先ゾーン選択肢 */
    dstZone(): Type1IpMappingItem["dstZone"][] {
      return this.zoneList
        .filter(
          (e) =>
            e.srcZoneType === "INTERNET" &&
            (e.dstZoneType === "EXTRA" || e.dstZoneType === "PRIVATE")
        )
        .map((e) => ({
          type: e.dstZoneType as "EXTRA" | "PRIVATE",
          zone: { zoneId: e.dstZoneId, zoneName: e.dstZoneName },
        }));
    },

    /** プライベートIP選択肢 */
    privateAddresses(): { ipAddressId: string; ipAddress: string }[] {
      // 選択された宛先ゾーンに属しているIPアドレス、ゾーン配置済み、/32のみ
      if (this.ipMappingInfo.dstZone) {
        if (this.ipMappingInfo.dstZone.type === "EXTRA") {
          return this.type1Extra.siteList
            .flatMap((e) => e.addressList)
            .filter((e) => e.zoneId)
            .filter((e) => e.ipAddress.endsWith("/32"))
            .sortBy([(e) => e.ipAddress, "asc"]);
        } else {
          return this.type1Private.lineList
            .flatMap((e1) => e1.siteList.flatMap((e2) => e2.addressList))
            .filter(
              (e) =>
                e.zoneId && e.zoneId === this.ipMappingInfo.dstZone!.zone.zoneId
            )
            .filter((e) => e.ipAddress.endsWith("/32"))
            .sortBy([(e) => e.ipAddress, "asc"]);
        }
      } else {
        return [];
      }
    },
    isPrivateByRange(): boolean | undefined {
      return this.ipMappingInfo.privatePort?.includes("-");
    },
    isGlobalByRange(): boolean | undefined {
      return this.ipMappingInfo.globalPort?.includes("-");
    },
  },
  async mounted() {
    if (!this.ipMapping) {
      this.globalIpList = (
        await this.$api.contract_change_type1.getType1Service()
      ).globalIpAddressList
        .map((e) => e.globalIpAddress)
        .sortBy([(e) => e, "asc"]);
      this.zoneList = (
        await this.$api.zoneRelation.getZoneRelation()
      ).zoneRelationList;
    }
    this.type1Extra = await this.$api.type1SiteZone.getType1ExtraList();
    this.type1Private = await this.$api.type1SiteZone.getType1PrivateList();
    this.isLoaded = true;
  },
  methods: {
    /**
     * ゾーン配置済で/32のアドレスリストを返却
     */
    zonePlacedAddresses(): { ipAddressId: string; ipAddress: string }[] {
      // ゾーン配置済み、/32のみ
      return this.type1Extra.siteList
        .flatMap((e) => e.addressList)
        .filter((e) => e.zoneId)
        .filter((e) => e.ipAddress.endsWith("/32"))
        .concat(
          this.type1Private.lineList
            .flatMap((e1) => e1.siteList.flatMap((e2) => e2.addressList))
            .filter((e) => e.zoneId)
            .filter((e) => e.ipAddress.endsWith("/32"))
        );
    },
    /**
     * IPマッピングの追加・変更を行う
     */
    async submit() {
      // propsから受け取るデータの有無で更新・追加処理をハンドリング
      if (this.ipMapping) {
        await this.$confirm("IPマッピングを変更します。よろしいですか？");
        // プライベートIPに設定されているIPアドレスがサイトに複数存在する場合、
        // ゾーン未配置のipAddressIdが渡される場合があるため
        // ゾーン配置済のipAddressIdに塗り替える必要がある

        // IPアドレス重複で且つipAddressIdがゾーン未配置の場合
        if (
          !this.ipMappingInfo.dstZone &&
          !this.zonePlacedAddresses().find(
            (e) => e.ipAddressId === this.ipMappingInfo.ipAddressId
          )
        ) {
          // ゾーン配置済のipAddressIdに塗り替える
          this.ipMappingInfo.ipAddressId = this.zonePlacedAddresses().find(
            (e) => e.ipAddress === this.ipMappingInfo.privateIpAddress
          )!.ipAddressId;
        }

        await this.$api.ipMapping.putType1IpMapping(
          this.ipMappingInfo.virtualIpAddressSeq,
          {
            description: this.ipMappingInfo.description || null,
            ipAddressId: this.ipMappingInfo.ipAddressId,
            ipMappingKeyId: this.ipMappingKeyId,
            note: null,
          }
        );
      } else {
        await this.$confirm("IPマッピングを追加します。よろしいですか？");
        await this.$api.ipMapping.postType1IpMapping({
          description: this.ipMappingInfo.description || null,
          ipMappingName: this.ipMappingInfo.ipMappingName,
          globalIpAddress: this.ipMappingInfo.globalIpAddress,
          ipAddressId: this.ipMappingInfo.ipAddressId,
          isPortForward: this.ipMappingInfo.isPortForward,
          protocol: this.ipMappingInfo.isPortForward
            ? this.ipMappingInfo.protocol
            : null,
          globalPort: this.ipMappingInfo.isPortForward
            ? this.ipMappingInfo.globalPort
            : null,
          privatePort: this.ipMappingInfo.isPortForward
            ? this.ipMappingInfo.privatePort
            : null,
          ipMappingKeyId: this.ipMappingKeyId,
          note: null,
        });
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
